import { Check, ChevronDown, ChevronLeft, ChevronUp, Images, LoaderCircle, ScanBarcode, VideoOff } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react'

import { ActionInfo, ActionInfoQuiet, Button, ScreenInfo, StatusMessage, useSwipeDismiss } from '@krp/ui-kit'

import { photoProblem } from '../../model/photo'
import { acceptsBarcode, isNewScan, type BarcodeCandidate, type BarcodeLookupVm } from '../../model/scanner'
import type { OpenCommonProduct, ProductPhotoUrl, ScannerActions, ScannerVm } from '../../model/types'
import { ProductTile } from '../../../../shared/ProductTile'
import type { ScanSessionVm } from '../../../scan-session/model/types'
import { ScanSessionBar } from '../../../scan-session/ui/ScanSessionBar'
import { SearchOutcome } from '../PhotoSearchScreen'
import { prepareCameraFrame, preparePhoto } from '../preparePhoto'
import { SearchProgressPanel } from '../SearchProgressPanel'
import { createBarcodeDecoder, type BarcodeDecoderFactory } from './barcodeDecoder'
import { useCameraStream } from './useCameraStream'
import { useUnzoomedFit } from './useUnzoomedFit'
import './scanner.css'

/** Справка экрана — единственная ⓘ, сверху слева, рядом с «Назад» (решение владельца 22.10.2026). */
export const SCANNER_SCREEN_INFO = {
  title: 'Сканер',
  lines: [
    'Наведите камеру на штрихкод — он прочитается сам.',
    'Штрихкода нет — наведите на товар и нажмите белую кнопку.',
    'Ответ появится снизу: «Это он?», «Выберите вариант» (несколько почти одинаковых товаров — сравните отличия и выберите свой), «Возможно, это», «Нет в базе» (тогда можно создать товар или прикрепить фото) или «Не удалось определить» — переснимите.',
    'Число рядом с товаром — процент совпадения со снимком. Вверху — счётчик смены: сколько создано, прикреплено, найдено и пропущено.',
  ],
} as const

/** Размер кадра, если `<video>` ещё не сообщил свой. */
const FALLBACK_FRAME = { width: 1280, height: 720 }

/** Кадр видео → PNG без потерь; дальше тот же путь поиска по фото. */
export type FrameGrabber = (video: HTMLVideoElement) => Promise<File | null>

const grabVideoFrame: FrameGrabber = (video) =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || FALLBACK_FRAME.width
    canvas.height = video.videoHeight || FALLBACK_FRAME.height
    const context = canvas.getContext('2d')
    if (context === null) {
      resolve(null)
      return
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob((blob) => resolve(blob === null ? null : new File([blob], 'scan.png', { type: 'image/png' })), 'image/png')
  })

function hintOf(lookup: BarcodeLookupVm): string {
  switch (lookup.kind) {
    case 'searching':
      return `Штрихкод ${lookup.code} — ищем в каталоге…`
    case 'missing':
      return `Штрихкода ${lookup.code} нет в каталоге — наведите камеру на товар и нажмите белую кнопку`
    default:
      return 'Наведите камеру на штрихкод. Штрихкода нет — наведите на товар и нажмите белую кнопку'
  }
}

function BarcodeResult({ lookup, photoUrl, onOpenProduct }: { lookup: BarcodeLookupVm; photoUrl: ProductPhotoUrl | undefined; onOpenProduct: OpenCommonProduct }) {
  switch (lookup.kind) {
    case 'none':
    case 'searching':
    case 'missing':
      return null
    case 'failed':
      return <StatusMessage kind="error" title={`Штрихкод ${lookup.code}: каталог не ответил`} text={lookup.message} />
    case 'found':
      return (
        <>
          <p aria-live="polite">
            Штрихкод {lookup.code}: найдено {lookup.products.length}
          </p>
          <ul className="platform-product-list" aria-label="Найдено по штрихкоду">
            {lookup.products.map((product) => (
              <ProductTile
                key={product.id}
                name={product.name}
                refs={product.refs}
                photoUrl={photoUrl}
                commonPhotoUrl={product.commonPhotoUrl}
                onOpen={() => onOpenProduct(product.id)}
                lines={[
                  { label: 'Артикул', value: product.sku ?? '—' },
                  { label: 'Штрихкод', value: lookup.code },
                ]}
              />
            ))}
          </ul>
        </>
      )
  }
}

/**
 * Есть ли что показать в листе найденного, и «подпись» этого: новая подпись — лист выезжает снова. Пока поиск по
 * снимку идёт, показывать нечего (RP-FB25): занят затвор, а лист выезжает с ответом — списком, пустым итогом или сбоем.
 * В подпись входит номер сканирования: новый код (или тот же после ухода из кадра) открывает лист заново, даже скрытый.
 */
function sheetTokenOf(vm: ScannerVm): string | null {
  const lookup = vm.lookup
  const barcode = lookup.kind === 'found' || lookup.kind === 'failed' ? `${lookup.kind}:${lookup.code}#${vm.scanId}` : ''
  const photo = vm.photo.kind === 'idle' || vm.photo.kind === 'searching' ? '' : vm.photo.kind
  return barcode === '' && photo === '' ? null : `${barcode}|${photo}`
}

/**
 * Штрихкод, прочитанный камерой и уже обработанный каталогом (найден / нет в каталоге / сбой). Поиск по снимку и
 * ожидание ответа — `null`: блок не рисуется и места не занимает. Лист найденного перекрывает нижнюю панель, так что
 * код виден, когда лист скрыт; пока он открыт, тот же код стоит в его строке «Штрихкод …: найдено».
 */
function scannedCodeOf(vm: ScannerVm): string | null {
  if (vm.photo.kind !== 'idle') return null
  const lookup = vm.lookup
  return lookup.kind === 'none' || lookup.kind === 'searching' ? null : lookup.code
}

/** Заголовок листа по итогу: «Найдено» — только когда что-то найдено, пустой итог и сбой так не называются. */
function sheetTitleOf(vm: ScannerVm): string {
  return vm.lookup.kind === 'found' ? 'Найдено' : 'Результат поиска'
}

/**
 * Сканер поиска по фото (RP-FB4; вид — как в старом клиенте, RP-FB14): живая задняя камера на весь экран, штрихкод
 * читается С ПОТОКА и уходит в общий каталог; штрихкода нет — человек наводит камеру на товар и жмёт белую кнопку
 * затвора, кадр уходит в поиск по фото. Найденное выезжает листом снизу поверх камеры: фото товара открывается на
 * весь экран, плашка — общую карточку товара (окно «Общий товар» поверх сканера). Лист смахивается вниз или закрывается кнопкой, и камера снова читает коды.
 * Окно `size: 'screen'`: поверхность, адрес и закрытие даёт оболочка. Камеры нет — объяснение и выбор снимка.
 */
export function ScannerScreen({
  vm,
  actions,
  photoUrl,
  onOpenProduct,
  sentShot = null,
  canSeeDetails = false,
  detailsExtra = null,
  session,
  decoderFactory = createBarcodeDecoder,
  grabFrame = grabVideoFrame,
}: {
  /** Смена сканирования (RP-FB56): счётчик «Создано · Прикреплено · Найдено · Пропущено» вверху; не задана — счётчика нет. */
  session?: ScanSessionVm | undefined
  vm: ScannerVm
  actions: ScannerActions
  photoUrl?: ProductPhotoUrl
  /** Открыть ОБЩУЮ карточку найденного товара (плашки штрихкода, поиска по снимку и лента смены): окно поверх сканера, камера остаётся под ним. */
  onOpenProduct: OpenCommonProduct
  /** Адрес отправленного снимка — вверху выдачи по фото (RP-FB26). */
  sentShot?: string | null
  /** Ссылка «Посмотреть детали поиска» — только администратору приложения (`system_admin`). */
  canSeeDetails?: boolean | undefined
  /** Время поиска по этапам внутри деталей поиска (администратору): узел собирает приложение, у него данные. */
  detailsExtra?: ReactNode
  /** Для тестов: jsdom камеры не имеет — подставляется декодер, выдающий коды сам. */
  decoderFactory?: BarcodeDecoderFactory
  grabFrame?: FrameGrabber
}) {
  const root = useRef<HTMLDivElement>(null)
  useUnzoomedFit(root)
  const video = useRef<HTMLVideoElement>(null)
  const filesInput = useRef<HTMLInputElement>(null)
  const status = useCameraStream(video)
  const lastSeen = useRef<BarcodeCandidate | null>(null)
  const candidate = useRef<BarcodeCandidate | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [hiddenFor, setHiddenFor] = useState<string | null>(null)
  /** Сколько раз код считан как НОВОЕ сканирование (RP-FB58): каждое показывает галочку заново, повтор того же кода — тоже. */
  const [readCount, setReadCount] = useState(0)
  const { code: send, photo, close } = actions
  const searching = vm.photo.kind === 'searching'

  const token = sheetTokenOf(vm)
  const sheetOpen = token !== null && hiddenFor !== token
  const sheetTitle = sheetTitleOf(vm)
  const hideSheet = useCallback(() => setHiddenFor(token), [token])
  const swipe = useSwipeDismiss({ axis: 'down', onDismiss: hideSheet, disabled: !sheetOpen })
  // Новый снимок — новый ответ: скрытый прежде лист выезжает снова, даже если итог того же вида («Найдено» за «Найдено»).
  const [wasSearching, setWasSearching] = useState(searching)
  if (searching !== wasSearching) {
    setWasSearching(searching)
    if (searching) setHiddenFor(null)
  }

  const onCode = useCallback(
    (raw: string) => {
      const now = Date.now()
      const accepted = acceptsBarcode(candidate.current, raw, now)
      candidate.current = { code: raw, at: now }
      if (!accepted) return
      // Пока код в кадре, камера отдаёт его каждый тик: «время последнего чтения» обновляется, и лист не дёргается.
      // Новым код считается, если он другой или уходил из кадра (`CODE_ABSENCE_MS`).
      const fresh = isNewScan(lastSeen.current, raw, now)
      lastSeen.current = { code: raw, at: now }
      if (!fresh) return
      setReadCount((count) => count + 1)
      send(raw)
    },
    [send],
  )

  // Кадры читаются всегда, и при открытом, и при скрытом листе: новый код (RP-FB4) сразу открывает лист с его товаром,
  // а непрерывное присутствие кода в кадре нужно, чтобы «тот же код» не принимался за новый после «Скрыть».
  const decoding = status === 'live'
  useEffect(() => {
    const element = video.current
    if (!decoding || element === null) return undefined
    const decoder = decoderFactory()
    decoder.start(element, onCode)
    return () => decoder.stop()
  }, [decoderFactory, decoding, onCode])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Поверх сканера открыто окно (карточка товара): Esc — его, сканер под ним неактивен.
      if (event.key !== 'Escape' || root.current?.closest('[inert]') != null) return
      if (sheetOpen) hideSheet()
      else close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [close, hideSheet, sheetOpen])

  const shoot = async () => {
    const element = video.current
    if (element === null) return
    const frame = await prepareCameraFrame(() => grabFrame(element))
    if (frame === null) {
      setProblem('Кадр не снялся — попробуйте ещё раз или выберите фото.')
      return
    }
    setProblem(null)
    photo(frame)
  }

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0]
    event.target.value = ''
    if (picked === undefined) return
    const early = photoProblem(picked)
    if (early !== null) {
      setProblem(early)
      return
    }
    const ready = await preparePhoto(picked)
    setProblem(null)
    photo(ready)
  }

  const scannedCode = scannedCodeOf(vm)
  const cameraProblem = status === 'denied' || status === 'unavailable'
  const shootOff = status !== 'live' || searching
  return (
    <ActionInfoQuiet>
      <div ref={root} className="platform-scanner">
        <div className="platform-scanner__view">
          {/* playsInline + muted — иначе iOS открывает поток на весь экран и не даёт кадры. */}
          <video ref={video} className="platform-scanner__video" playsInline muted autoPlay />
          {cameraProblem ? (
            <div className="platform-scanner__problem" role="alert">
              <VideoOff size={24} aria-hidden="true" />
              <p>
                {status === 'denied'
                  ? 'Доступ к камере запрещён. Разрешите его в настройках браузера для этого сайта — или выберите фото.'
                  : 'Браузер не даёт поток камеры (нужен HTTPS и разрешение). Выберите фото товара.'}
              </p>
            </div>
          ) : (
            <div className="platform-scanner__frame" aria-hidden="true" />
          )}

          <div className="platform-scanner__bar">
            <Button variant="ghost" icon={<ChevronLeft size={20} aria-hidden="true" />} onClick={close} actionId="platform.search.scanner-close">
              Назад
            </Button>
            <ScreenInfo title={SCANNER_SCREEN_INFO.title} lines={SCANNER_SCREEN_INFO.lines} />
            <span className="platform-scanner__title">Сканер</span>
            <span className="platform-scanner__live" data-status={status}>
              {status === 'live' ? 'камера' : status === 'starting' ? 'запуск…' : 'нет камеры'}
            </span>
          </div>

          {session !== undefined && (
            <div className="platform-scanner__session">
              <ScanSessionBar vm={session} surface="camera" onOpenProduct={onOpenProduct} />
            </div>
          )}

          {/* Ход поиска — отдельный лист поверх видоискателя между шапкой и нижней панелью: затвор и боковые кнопки не двигаются. */}
          {searching && (
            <div className="platform-scanner__run">
              <SearchProgressPanel onCancel={actions.cancel} shotUrl={sentShot} />
            </div>
          )}

          <div className="platform-scanner__bottom">
            {scannedCode !== null && (
              <div className="platform-scanner__code" role="group" aria-label={`найден штрихкод ${scannedCode}`}>
                <ScanBarcode size={22} aria-hidden="true" />
                <span className="platform-scanner__code-value">{scannedCode}</span>
              </div>
            )}
            {problem !== null ? (
              <p className="platform-scanner__hint" role="alert">
                {problem}
              </p>
            ) : (
              !cameraProblem &&
              !searching && (
                <p className="platform-scanner__hint" role="status">
                  <ScanBarcode size={16} aria-hidden="true" /> {hintOf(vm.lookup)}
                </p>
              )
            )}
            <div className="platform-scanner__controls">
              <div className="platform-scanner__side platform-scanner__side--start" data-slot="left">
                <ActionInfo id="platform.search.scanner-file" label="Выбрать фото" disabled={searching}>
                  <input ref={filesInput} type="file" accept="image/*" hidden aria-label="Фото товара для сканера" onChange={(event) => void onPick(event)} />
                  <Button
                    variant="secondary"
                    icon={<Images size={18} aria-hidden="true" />}
                    disabled={searching}
                    onClick={() => filesInput.current?.click()}
                  >
                    Выбрать фото
                  </Button>
                </ActionInfo>
              </div>
              <div className="platform-scanner__center" data-slot="center">
              <ActionInfo
                id="platform.search.scanner-shoot"
                label="Снять"
                disabled={shootOff}
                disabledReason={status !== 'live' ? 'Камера не работает — выберите фото.' : searching ? 'Поиск по прошлому снимку ещё идёт.' : null}
              >
                <button
                  type="button"
                  className="platform-scanner__shutter"
                  aria-label="Снять"
                  aria-busy={searching || undefined}
                  disabled={shootOff}
                  onClick={() => void shoot()}
                >
                  {searching && <LoaderCircle className="platform-scanner__spinner" size={32} aria-hidden="true" />}
                </button>
              </ActionInfo>
              </div>
              <div className="platform-scanner__side platform-scanner__side--end" data-slot="right">
                {token !== null && !sheetOpen && (
                  <Button
                    variant="secondary"
                    icon={<ChevronUp size={18} aria-hidden="true" />}
                    onClick={() => setHiddenFor(null)}
                    actionId="platform.search.scanner-sheet-open"
                  >
                    Найденное
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Галочка «Код считан» по центру видоискателя; новый key на каждое чтение перезапускает анимацию, в том числе на повторе того же кода. */}
        {readCount > 0 && (
          <div key={readCount} className="platform-scanner__read" data-read={readCount} aria-hidden="true">
            <Check size={72} strokeWidth={3} />
            <span>Код считан</span>
          </div>
        )}
        <p className="platform-scanner__sr" aria-live="polite">
          {readCount > 0 ? `Код считан, сканирование ${readCount}` : ''}
        </p>

        {sheetOpen && (
          <>
            <div className="platform-scanner__backdrop" aria-hidden="true" onClick={hideSheet} />
            <section className="platform-scanner__sheet" aria-label={sheetTitle} style={swipe.style}>
              <div className="platform-scanner__sheet-head" {...swipe.bind()}>
                <span className="platform-scanner__grip" aria-hidden="true" />
                <strong className="platform-scanner__sheet-title">{sheetTitle}</strong>
                <Button
                  variant="ghost"
                  icon={<ChevronDown size={18} aria-hidden="true" />}
                  onClick={hideSheet}
                  actionId="platform.search.scanner-sheet-close"
                >
                  Скрыть
                </Button>
              </div>
              <div className="platform-scanner__sheet-body">
                <BarcodeResult lookup={vm.lookup} photoUrl={photoUrl} onOpenProduct={onOpenProduct} />
                {vm.photo.kind !== 'idle' && <SearchProgressPanel />}
                {vm.photo.kind !== 'idle' && (
                  <SearchOutcome
                    outcome={vm.photo}
                    photoUrl={photoUrl}
                    onOpenProduct={onOpenProduct}
                    sentShot={sentShot}
                    canSeeDetails={canSeeDetails}
                    detailsExtra={detailsExtra}
                    onRetry={actions.retry}
                    onPickAnother={() => filesInput.current?.click()}
                    verdictActions={actions.verdict}
                    onRetake={hideSheet}
                    retakeLabel="Снять ещё раз"
                  />
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </ActionInfoQuiet>
  )
}
